import { ApiService } from "./api.service";
import type {
  ICalendarSource,
  IGoogleDomainWideConfig,
  IHolidayCalendar,
  IHolidayCalendarDate,
  IHolidayCalendarDateImportResult,
  IHolidayCalendarDatePage,
  IHolidayCalendarDatePatch,
  IHolidayCalendarDateQuery,
  IHolidayCalendarDateWrite,
  IHolidayCalendarPerson,
  IHolidayCalendarSubscription,
  IHolidayCalendarSuggestion,
  IHolidayCalendarSyncResult,
  IHolidayCalendarWrite,
  IHolidayCountry,
  IOutlookDomainWideConfig,
} from "@/types/workspace";
import type { paths } from "@/types/api.generated";

/**
 * The Google domain-wide calendar route, as the generated OpenAPI types see it
 * (`app/types/api.generated.ts` ← `apps/backend/schema.yml`; regenerate with
 * `pnpm gen:api`). Reaching through `paths` rather than naming the component
 * ties the request body to the URL AND the method, so renaming the route or
 * dropping a serializer field fails `pnpm check:types` here rather than 400ing
 * in production.
 */
type GoogleDomainWideRoute = paths["/api/workspaces/{slug}/calendar-sources/google-domain-wide/"];

/** PATCH body for the Google domain-wide config — the settable subset. */
type GoogleDomainWideConfigPatch = NonNullable<
  GoogleDomainWideRoute["patch"]["requestBody"]
>["content"]["application/json"];

export const OAUTH_PROVIDERS = new Set<ICalendarSource["source_type"]>(["google", "outlook"]);
export const MANAGED_USER_SOURCE_TYPES = new Set<ICalendarSource["source_type"]>([
  "google_domain_wide",
  "outlook_domain_wide",
  "manual_time_off",
  "public_holidays",
]);

export class WorkspaceCalendarService extends ApiService {
  async listUserSources(): Promise<ICalendarSource[]> {
    return this.get("/api/users/me/calendar-sources/");
  }

  async uploadUserICS(file: File, name: string, workspaceSlug?: string): Promise<ICalendarSource> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    if (workspaceSlug) {
      formData.append("workspace_slug", workspaceSlug);
    }
    return this.post("/api/users/me/calendar-sources/ics-upload/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async deleteUserSource(sourceId: string): Promise<void> {
    return this.delete(`/api/users/me/calendar-sources/${sourceId}/`);
  }

  async listHolidayRegions(): Promise<{ countries: IHolidayCountry[] }> {
    return this.get("/api/holiday-regions/");
  }

  // --- Admin-managed holiday calendars (M4–M6) ---

  async listHolidayCalendars(slug: string): Promise<IHolidayCalendar[]> {
    return this.get(`/api/workspaces/${slug}/holiday-calendars/`);
  }

  async createHolidayCalendar(
    slug: string,
    data: IHolidayCalendarWrite,
  ): Promise<IHolidayCalendar> {
    return this.post(`/api/workspaces/${slug}/holiday-calendars/`, data);
  }

  async getHolidayCalendar(slug: string, calendarId: string): Promise<IHolidayCalendar> {
    return this.get(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/`);
  }

  async updateHolidayCalendar(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarWrite,
  ): Promise<IHolidayCalendar> {
    return this.patch(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/`, data);
  }

  async deleteHolidayCalendar(slug: string, calendarId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/`);
  }

  async getHolidayCalendarPeople(
    slug: string,
    calendarId: string,
  ): Promise<{ results: IHolidayCalendarPerson[]; total_count: number }> {
    return this.get(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/people/`);
  }

  // --- Suggested calendars (M15) ---

  async listHolidayCalendarSuggestions(slug: string): Promise<IHolidayCalendarSuggestion[]> {
    return this.get(`/api/workspaces/${slug}/holiday-calendar-suggestions/`);
  }

  async dismissHolidayCalendarSuggestion(slug: string, key: string): Promise<{ key: string }> {
    return this.post(`/api/workspaces/${slug}/holiday-calendar-suggestions/dismissals/`, { key });
  }

  async publishHolidayCalendar(slug: string, calendarId: string): Promise<IHolidayCalendar> {
    return this.post(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/publish/`, {});
  }

  async discardHolidayCalendarDates(slug: string, calendarId: string): Promise<IHolidayCalendar> {
    return this.post(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/discard/`, {});
  }

  // --- A calendar's dates (M7) ---

  async listHolidayCalendarDates(
    slug: string,
    calendarId: string,
    query: IHolidayCalendarDateQuery = {},
  ): Promise<IHolidayCalendarDatePage> {
    const params = new URLSearchParams();
    if (query.scope) params.set("scope", query.scope);
    if (query.search) params.set("search", query.search);
    if (query.sort_by) params.set("sort_by", query.sort_by);
    if (query.sort_order) params.set("sort_order", query.sort_order);
    if (query.cursor) params.set("cursor", query.cursor);
    if (query.per_page != null) params.set("per_page", String(query.per_page));
    const suffix = params.size > 0 ? `?${params.toString()}` : "";
    return this.get(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/dates/${suffix}`);
  }

  async createHolidayCalendarDate(
    slug: string,
    calendarId: string,
    data: IHolidayCalendarDateWrite,
  ): Promise<IHolidayCalendarDate> {
    return this.post(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/dates/`, data);
  }

  /** Bulk-import a calendar's dates from an .ics file (M8). */
  async importHolidayCalendarDates(
    slug: string,
    calendarId: string,
    file: File,
  ): Promise<IHolidayCalendarDateImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    return this.post(
      `/api/workspaces/${slug}/holiday-calendars/${calendarId}/dates/import/`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  }

  /** Rename a date, or exclude / re-include a synced one (M9). */
  async patchHolidayCalendarDate(
    slug: string,
    calendarId: string,
    dateId: string,
    data: IHolidayCalendarDatePatch,
  ): Promise<IHolidayCalendarDate> {
    return this.patch(
      `/api/workspaces/${slug}/holiday-calendars/${calendarId}/dates/${dateId}/`,
      data,
    );
  }

  async deleteHolidayCalendarDate(slug: string, calendarId: string, dateId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/dates/${dateId}/`);
  }

  // --- The live ICS URL subscription (M9) ---

  async connectHolidayCalendarFeed(
    slug: string,
    calendarId: string,
    feedUrl: string,
  ): Promise<{ subscription: IHolidayCalendarSubscription; synced_count: number }> {
    return this.post(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/subscription/`, {
      feed_url: feedUrl,
    });
  }

  async syncHolidayCalendarFeed(
    slug: string,
    calendarId: string,
  ): Promise<IHolidayCalendarSyncResult> {
    return this.post(
      `/api/workspaces/${slug}/holiday-calendars/${calendarId}/subscription/sync/`,
      {},
    );
  }

  async disconnectHolidayCalendarFeed(slug: string, calendarId: string): Promise<{ kept: number }> {
    return this.delete(`/api/workspaces/${slug}/holiday-calendars/${calendarId}/subscription/`);
  }

  async getMyHolidayCalendar(slug: string): Promise<ICalendarSource | null> {
    return this.get(`/api/workspaces/${slug}/holiday-calendar/me/`);
  }

  async setMyHolidayCalendar(
    slug: string,
    data: { country: string; subdivision?: string },
  ): Promise<ICalendarSource> {
    return this.post(`/api/workspaces/${slug}/holiday-calendar/me/`, data);
  }

  async deleteMyHolidayCalendar(slug: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/holiday-calendar/me/`);
  }

  async getOAuthConnectUrl(
    provider: "google" | "outlook",
    returnPath?: string,
  ): Promise<{ authorization_url: string }> {
    const params = returnPath ? { return_path: returnPath } : undefined;
    return this.get(`/api/users/me/calendar-connect/${provider}/`, { params });
  }

  async syncUserSource(sourceId: string): Promise<{ status: string }> {
    return this.post(`/api/users/me/calendar-sources/${sourceId}/sync/`, {});
  }

  async disconnectUserSource(sourceId: string): Promise<void> {
    return this.delete(`/api/users/me/calendar-sources/${sourceId}/disconnect/`);
  }

  async getGoogleDomainWideConfig(
    slug: string,
    params?: { member_offset?: number; member_limit?: number },
  ): Promise<IGoogleDomainWideConfig | Record<string, never>> {
    return this.get(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/`, { params });
  }

  async createGoogleDomainWideConfig(
    slug: string,
    data: { service_account_key: string; domain: string },
  ): Promise<IGoogleDomainWideConfig> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/`, data);
  }

  async updateGoogleDomainWideConfig(
    slug: string,
    data: GoogleDomainWideConfigPatch,
  ): Promise<IGoogleDomainWideConfig> {
    return this.patch(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/`, data);
  }

  async deleteGoogleDomainWideConfig(slug: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/`);
  }

  async testGoogleDomainWideConfig(
    slug: string,
    data: { test_email: string },
  ): Promise<{ success: boolean; message: string }> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/test/`, data);
  }

  async syncGoogleDomainWideConfig(slug: string): Promise<{ status: string }> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/google-domain-wide/sync/`, {});
  }

  async getOutlookDomainWideConfig(
    slug: string,
    params?: { member_offset?: number; member_limit?: number },
  ): Promise<IOutlookDomainWideConfig | Record<string, never>> {
    return this.get(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/`, { params });
  }

  async createOutlookDomainWideConfig(
    slug: string,
    data: { tenant_id: string; client_id: string; client_secret: string; domain: string },
  ): Promise<IOutlookDomainWideConfig> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/`, data);
  }

  async updateOutlookDomainWideConfig(
    slug: string,
    data: Partial<{ is_enabled: boolean; domain: string }>,
  ): Promise<IOutlookDomainWideConfig> {
    return this.patch(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/`, data);
  }

  async deleteOutlookDomainWideConfig(slug: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/`);
  }

  async testOutlookDomainWideConfig(
    slug: string,
    data: { test_email: string },
  ): Promise<{ success: boolean; message: string }> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/test/`, data);
  }

  async syncOutlookDomainWideConfig(slug: string): Promise<{ status: string }> {
    return this.post(`/api/workspaces/${slug}/calendar-sources/outlook-domain-wide/sync/`, {});
  }
}
